import api from '../apiService';
import { LibraryPanelHost } from '../components/chat/LibraryPanel';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { classificationBase, readClassificationContext, ClassificationContext, selectedDealShare } from '../classification/transport';
import { useClassificationAccess } from '../classification/useClassificationAccess';
import ClassificationFrame from '../classification/ClassificationFrame';
import ClassificationHome from '../classification/ClassificationHome';

export default function ClassificationRoute({ openVersion = 0 }: {
  openVersion?: number;
}) {
  const { projectId } = useParams();
  const location = useLocation();
  const [agentWidth, setAgentWidth] = React.useState(0);
  const [referenceWidth, setReferenceWidth] = React.useState(0);
  const sharedSelection = React.useMemo(() => {
    try { return { id: selectedDealShare(location.search), error: '' }; }
    catch (error) { return { id: undefined, error: (error as Error).message }; }
  }, [location.search]);
  const { authToken, userData } = useAuth();
  const [attempt, retry] = React.useReducer(value => value + 1, 0);
  const access = useClassificationAccess(attempt);
  const [state, setState] = React.useState<{ key: string; ready?: boolean; base?: string; context?: ClassificationContext; error?: string }>({ key: '' });
  const identity = `${userData?.uid || userData?.id || ''}:${userData?.email || ''}:${userData?.company || ''}:${authToken || ''}`;
  const key = `${identity}:${projectId || ''}:${sharedSelection.id || ''}:${attempt}:${openVersion}`;
  React.useEffect(() => {
    const controller = new AbortController();
    if (!access.enabled || !projectId) return () => controller.abort();
    const load = async () => {
      if (sharedSelection.error) throw new Error(sharedSelection.error);
      if (sharedSelection.id && !projectId) throw new Error('This shared deal link does not identify its project.');
      if (projectId) {
        const context = await readClassificationContext(projectId, controller.signal, sharedSelection.id);
        const base = context.transport?.protocol === 'bark-classification-v1' ? await classificationBase() : undefined;
        if (!controller.signal.aborted) {
          setState({ key, ready: true, base, context });
        }
      }
    };
    load().catch(error => { if (!controller.signal.aborted) setState({ key, error: error.message }); });
    return () => controller.abort();
  }, [access.enabled, projectId, key, userData?.email, userData?.uid, sharedSelection.id, sharedSelection.error]);
  const current = state.key === key ? state : null;
  const error = access.error || sharedSelection.error || (!projectId && sharedSelection.id ? 'This shared deal link does not identify its project.' : undefined) || current?.error;
  if (!projectId && !sharedSelection.error && !sharedSelection.id && access.enabled) return <ClassificationHome key={identity} openVersion={openVersion} />;
  return <section className="h-full flex flex-col bg-surface overflow-hidden" aria-label="Classification">
    {!projectId && <header className="border-b border-border-default px-6 py-4">
      <h1 className="text-lg font-semibold text-fg">Credit classification</h1>
    </header>}
    {error ? <div role="alert" className="m-6 p-3 border border-danger bg-danger-bg rounded-md">
      <p className="text-sm text-danger">{error}</p>
      <button className="text-sm text-accent underline" onClick={retry}>Retry</button>
    </div> : current?.base && current.context && projectId ? <div className="flex-1 min-h-0"><LibraryPanelHost origin={api.getLibraryOrigin()} dock={{right: agentWidth, onWidth: setReferenceWidth}}><ClassificationFrame key={`${identity}:${projectId}:${sharedSelection.id || ''}`} base={current.base} context={current.context} referenceWidth={referenceWidth} onAgentWidth={setAgentWidth} /></LibraryPanelHost></div>
      : current?.ready ? <p role="status" className="p-6 text-sm text-fg-secondary">The Classification workspace is unavailable in this environment.</p>
      : <p role="status" className="p-6 text-sm text-fg-secondary">Loading Classification…</p>}
  </section>;
}
